import { useMemo } from "react";
import BreakdownRow from "./BreakdownRow";
import { MapPin } from "lucide-react";

/**
 * Scene-grouped, editable preview of parsed shot rows. Rows are grouped by their
 * derived scene_number (contiguous INT./EXT. runs) with a heading per group.
 * `onRowChange(index, newRow)` patches a single row in the parent's state.
 */
export default function BreakdownPreviewTable({ rows, scenes, onRowChange, issuesOnly }) {
  const sceneHeadingByNumber = useMemo(() => {
    const map = {};
    (scenes || []).forEach((s) => { map[s.number] = s.heading; });
    return map;
  }, [scenes]);

  // group row indices by scene_number, preserving order
  const groups = useMemo(() => {
    const out = [];
    let current = null;
    rows.forEach((row, idx) => {
      if (issuesOnly && row.confidence !== "low") return;
      if (!current || current.scene !== row.scene_number) {
        current = { scene: row.scene_number, heading: sceneHeadingByNumber[row.scene_number] || `Scene ${row.scene_number}`, items: [] };
        out.push(current);
      }
      current.items.push({ row, idx });
    });
    return out;
  }, [rows, issuesOnly, sceneHeadingByNumber]);

  if (groups.length === 0) {
    return (
      <div className="py-10 text-center text-sm text-[var(--text-muted)]">
        {issuesOnly ? "No rows need review — all parsed confidently." : "No shots to display."}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <div key={`sc-${g.scene}`}>
          <div className="flex items-center gap-1.5 mb-1.5 px-1">
            <MapPin size={11} className="text-[var(--text-muted)]" />
            <span className="text-[11px] font-medium text-[var(--text-secondary)] uppercase tracking-wide">
              {g.heading}
            </span>
            <span className="text-[10px] text-[var(--text-muted)]">· {g.items.length} shot{g.items.length !== 1 ? "s" : ""}</span>
          </div>
          <div className="space-y-1.5">
            {g.items.map(({ row, idx }) => (
              <BreakdownRow
                key={idx}
                row={row}
                onChange={(nr) => onRowChange(idx, nr)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
